/* ===========================================================
   Wholesale Matcha — Landing Page behavior
   Asset path: assets/wholesale-matcha.js
   Page: /pages/wholesale-matcha

   EDGE-CACHE RULE (Nostra): every per-visitor decision is made HERE, never in
   Liquid, so the origin HTML (incl. ?contact_posted=true) is byte-identical for
   every visitor and a cached response cannot leak one visitor's state.

   CONVERSION RULE: generate_lead fires ONLY from a sessionStorage record this
   browser wrote at submit time; a typed or crawled ?contact_posted=true fires
   nothing. Google Search bids on it, so a replayable conversion is a budget bug.

   INTAKE RULE: the application posts to the dashboard intake, the ONLY place the
   EIN goes. Any failure falls back to the native post, which never carries the
   EIN. Tested by scripts/test-wholesale-form-post.js.
   =========================================================== */

(function () {
  'use strict';

  var root = document.querySelector('[data-wm-root]');
  if (!root) return;

  var cfg = {};
  try {
    var cfgEl = document.querySelector('[data-wm-config]');
    if (cfgEl) cfg = JSON.parse(cfgEl.textContent) || {};
  } catch (e) { cfg = {}; }
  cfg.klaviyo = cfg.klaviyo || {};
  cfg.intake = cfg.intake || {};

  var STASH_KEY = 'wm_lead_pending';
  var STASH_TTL_MS = 15 * 60 * 1000;

  /* ---------- analytics ---------- */

  function assign(target, src) {
    target = target || {};
    for (var k in src) { if (Object.prototype.hasOwnProperty.call(src, k)) target[k] = src[k]; }
    return target;
  }

  // Via the GTM web container, NOT window.ElevarDataLayer (its schema covers
  // ecommerce dl_* events; an unmapped push risks a double count).
  function track(name, params) {
    params = params || {};
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(assign({ event: name }, params));
    } catch (e) {}
    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, params);
    } catch (e) {}
  }

  /* ---------- click-id capture (first-party, never a URL param) ---------- */

  function captureClickIds() {
    try {
      var qs = new URLSearchParams(window.location.search);
      var ids = ['gclid', 'gbraid', 'wbraid'];
      for (var i = 0; i < ids.length; i++) {
        var v = qs.get(ids[i]);
        if (v) {
          localStorage.setItem('wm_' + ids[i], JSON.stringify({ v: v, t: Date.now() }));
        }
      }
    } catch (e) {}
  }

  function readClickId() {
    var ids = ['gclid', 'gbraid', 'wbraid'];
    for (var i = 0; i < ids.length; i++) {
      try {
        var raw = localStorage.getItem('wm_' + ids[i]);
        if (!raw) continue;
        var rec = JSON.parse(raw);
        // Google Ads click ids stay valid for 90 days.
        if (rec && rec.v && (Date.now() - rec.t) < 90 * 24 * 60 * 60 * 1000) {
          return { type: ids[i], value: rec.v };
        }
      } catch (e) {}
    }
    return null;
  }

  /* ---------- Klaviyo (degrades silently when unconfigured) ---------- */

  // NOTE: the EIN is never sent to Klaviyo, GA4, or Google Ads. Only a boolean.
  function klaviyoPost(path, data) {
    fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(cfg.klaviyo.companyId || ''), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: cfg.klaviyo.revision || '2025-10-15' },
      body: JSON.stringify({ data: data })
    }).catch(function () {});
  }

  function klaviyoLead(email, props) {
    if (!cfg.klaviyo.companyId) return;
    var profile = { data: { type: 'profile', attributes: { email: email } } };
    try {
      // Applications are not marketing opt-in. The dashboard owns list membership.
      klaviyoPost('events', {
        type: 'event',
        attributes: {
          properties: props || {},
          metric: { data: { type: 'metric', attributes: { name: 'Wholesale Matcha Application' } } },
          profile: profile
        }
      });
    } catch (e) {}
  }

  /* ---------- EIN validation ---------- */

  // A US EIN is 9 digits (NN-NNNNNNN). No public IRS API: format check only.
  // Never block on the prefix list; the IRS revises it.
  var EIN_DIGITS = /^\d{9}$/;

  function einDigits(value) {
    return (value || '').replace(/[^0-9]/g, '');
  }

  function formatEin(value) {
    var d = einDigits(value).slice(0, 9);
    if (d.length > 2) return d.slice(0, 2) + '-' + d.slice(2);
    return d;
  }

  /* ---------- form ---------- */

  // Targeted by id: hyphenated data-* keys are not reliable in a Liquid {% form %} tag.
  var form = root.querySelector('#wholesale-application-form');
  var successPanel = root.querySelector('[data-wm-success]');
  var errorSummary = root.querySelector('[data-wm-error-summary]');
  var errorList = errorSummary ? errorSummary.querySelector('ul') : null;
  var startFired = false;
  var submitting = false;

  function fieldError(field, message) {
    var wrap = field.closest('.wm-field') || field.parentNode;
    var slot = wrap.querySelector('[data-wm-error]');
    if (message) {
      field.setAttribute('aria-invalid', 'true');
      if (slot) slot.textContent = message;
    } else {
      field.removeAttribute('aria-invalid');
      if (slot) slot.textContent = '';
    }
  }

  function labelFor(field) {
    var wrap = field.closest('.wm-field');
    var label = wrap ? wrap.querySelector('label') : null;
    if (!label) return field.name;
    // Drop the nested hint span, or it lands in the error message.
    var clone = label.cloneNode(true);
    var hint = clone.querySelector('.wm-hint');
    if (hint) hint.parentNode.removeChild(hint);
    return clone.textContent.replace(/\*/g, '').replace(/\s+/g, ' ').trim();
  }

  function validate() {
    if (!form) return [];
    var problems = [];
    var fields = form.querySelectorAll('[data-wm-required]');

    for (var i = 0; i < fields.length; i++) {
      var f = fields[i];
      fieldError(f, '');
      var val = (f.value || '').trim();

      if (!val) {
        fieldError(f, 'This field is required.');
        problems.push({ field: f, message: labelFor(f) + ' is required.' });
        continue;
      }
      if (f.type === 'email' && val.indexOf('@') === -1) {
        fieldError(f, 'Enter a valid email address.');
        problems.push({ field: f, message: 'Enter a valid email address.' });
      }
    }

    // EIN: skipped entirely when the outside-the-US escape hatch is checked.
    var ein = form.querySelector('[data-wm-ein]');
    var outsideUs = form.querySelector('[data-wm-outside-us]');
    if (ein && !(outsideUs && outsideUs.checked)) {
      var digits = einDigits(ein.value);
      if (digits && !EIN_DIGITS.test(digits)) {
        fieldError(ein, 'A US EIN is nine digits, written as 12-3456789.');
        problems.push({ field: ein, message: 'Check the EIN. It should be nine digits.' });
        track('wholesale_form_field_error', { field_name: 'ein', error_type: 'format' });
      }
    }

    return problems;
  }

  function showErrorSummary(problems) {
    if (!errorSummary || !errorList) return;
    if (!problems.length) {
      errorSummary.hidden = true;
      errorList.innerHTML = '';
      return;
    }
    errorList.innerHTML = '';
    for (var i = 0; i < problems.length; i++) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      var id = problems[i].field.id;
      a.href = '#' + id;
      a.textContent = problems[i].message;
      (function (target) {
        a.addEventListener('click', function (ev) {
          ev.preventDefault();
          target.focus();
        });
      })(problems[i].field);
      li.appendChild(a);
      errorList.appendChild(li);
    }
    errorSummary.hidden = false;
    // Focusing the summary announces it. role="alert" on top would double-announce.
    errorSummary.focus();
  }

  if (form) {
    // JS owns validation. novalidate is set here, not in markup, so the
    // browser's own validation remains if this asset fails to load.
    form.setAttribute('novalidate', 'novalidate');

    var stamp = form.querySelector('[data-wm-stamp]');
    if (stamp) stamp.value = String(Date.now());

    form.addEventListener('focusin', function () {
      if (startFired) return;
      startFired = true;
      // Not form_start: GA4 Enhanced Measurement sends its own and would collide.
      track('wholesale_form_start', { form_id: 'wholesale-application' });
    });

    var einInput = form.querySelector('[data-wm-ein]');
    var outsideUsInput = form.querySelector('[data-wm-outside-us]');

    if (einInput) {
      einInput.addEventListener('input', function () {
        // US EIN only: digit-stripping would mangle a foreign registration
        // number (a Canadian BN, a UK company number).
        if (outsideUsInput && outsideUsInput.checked) return;
        var pos = einInput.selectionStart;
        var before = einInput.value.length;
        einInput.value = formatEin(einInput.value);
        var after = einInput.value.length;
        try { einInput.setSelectionRange(pos + (after - before), pos + (after - before)); } catch (e) {}
      });
    }

    if (outsideUsInput && einInput) {
      outsideUsInput.addEventListener('change', function () {
        var label = form.querySelector('[data-wm-ein-label-text]');
        var hint = form.querySelector('[data-wm-ein-hint]');
        if (outsideUsInput.checked) {
          if (label) label.textContent = 'Business registration number';
          if (hint) hint.textContent = 'Your local business or tax registration number.';
          einInput.removeAttribute('data-wm-ein');
          einInput.setAttribute('data-wm-ein-disabled', 'true');
        } else {
          if (label) label.textContent = 'EIN';
          if (hint) hint.textContent = 'Your nine-digit federal tax ID. This is what puts you on the fast track.';
          einInput.setAttribute('data-wm-ein', 'true');
          einInput.removeAttribute('data-wm-ein-disabled');
        }
        fieldError(einInput, '');
      });
    }

    form.addEventListener('submit', function (ev) {
      if (submitting) { ev.preventDefault(); return; }

      var problems = validate();
      if (problems.length) {
        ev.preventDefault();
        showErrorSummary(problems);
        if (problems[0] && problems[0].field) problems[0].field.focus();
        track('wholesale_application_error', { error_count: problems.length });
        return;
      }
      showErrorSummary([]);
      // Never let a template edit put the EIN back into the native post.
      var tax = form.querySelector('#wm-ein');
      if (tax) tax.removeAttribute('name');

      // Write the conversion stash BEFORE the page unloads. Only a browser that
      // actually submitted can fire generate_lead on the way back.
      var emailField = form.querySelector('[name="contact[email]"]');
      var typeField = form.querySelector('[name="contact[Business type]"]');
      var volumeField = form.querySelector('[name="contact[Estimated monthly matcha volume]"]');
      var einField = form.querySelector('[data-wm-ein], [data-wm-ein-disabled]');
      var clickId = readClickId();

      try {
        sessionStorage.setItem(STASH_KEY, JSON.stringify({
          t: Date.now(),
          email: emailField ? emailField.value.trim() : '',
          business_type: typeField ? typeField.value : '',
          volume_band: volumeField ? volumeField.value : '',
          ein_provided: !!(einField && einField.value.trim()),
          click_id_type: clickId ? clickId.type : '',
          click_id: clickId ? clickId.value : ''
        }));
      } catch (e) {}

      submitting = true;
      // Disabling synchronously can cancel the submission in some browsers.
      var btn = form.querySelector('[data-wm-submit]');
      if (btn) {
        window.setTimeout(function () {
          btn.disabled = true;
          btn.textContent = btn.getAttribute('data-wm-submitting-label') || 'Sending...';
        }, 0);
      }

      // Intake path; unconfigured or no fetch = the native post, as before.
      if (!cfg.intake.url || typeof window.fetch !== 'function') return;
      ev.preventDefault();
      postToIntake(buildIntakePayload(clickId)).then(function (applicationId) {
        completeLead(readStash(), { submit_path: 'intake', application_id: applicationId });
      }, function () {
        nativeSubmit();
      });
    });
  }

  /* ---------- intake ---------- */

  function fieldValue(selector) {
    var el = form ? form.querySelector(selector) : null;
    return el ? String(el.value || '').trim() : '';
  }

  // The ONE payload that carries the EIN. Payload key -> contact[...] field name.
  var INTAKE_FIELDS = { business_name: 'Business name', business_type: 'Business type',
    volume: 'Estimated monthly matcha volume', website: 'Business website', contact_name: 'name',
    phone: 'phone', email: 'email', notes: 'body', hp: 'Website' };
  function buildIntakePayload(clickId) {
    var out = {}, outside = form.querySelector('[data-wm-outside-us]');
    for (var k in INTAKE_FIELDS) out[k] = fieldValue('[name="contact[' + INTAKE_FIELDS[k] + ']"]');
    out.ein = fieldValue('#wm-ein');
    out.outside_us = !!(outside && outside.checked);
    out.source = window.location.pathname;
    out.click_id_type = clickId ? clickId.type : '';
    out.click_id = clickId ? clickId.value : '';
    return out;
  }

  // Resolves with the id on 200 { ok: true }; rejects on anything else or a timeout.
  function postToIntake(payload) {
    return new Promise(function (resolve, reject) {
      var ctl = window.AbortController ? new AbortController() : null;
      var timer = window.setTimeout(function () {
        reject(new Error('timeout'));
        if (ctl) ctl.abort();
      }, Number(cfg.intake.timeoutMs) || 8000);
      window.fetch(cfg.intake.url, {
        method: 'POST', mode: 'cors', credentials: 'omit', signal: ctl ? ctl.signal : undefined,
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      }).then(function (res) {
        if (!res.ok) throw new Error('status ' + res.status);
        return res.json();
      }).then(function (body) {
        if (!body || body.ok !== true) throw new Error('not ok');
        resolve(body.id || '');
      }).catch(reject).then(function () { window.clearTimeout(timer); });
    });
  }

  // Native post (no EIN), bypassing this handler; the stash survives for handleReturn.
  function nativeSubmit() {
    track('wholesale_intake_fallback', { form_id: 'wholesale-application' });
    try { HTMLFormElement.prototype.submit.call(form); } catch (e) { form.submit(); }
  }

  /* ---------- success state (client-side only) ---------- */

  // The panel ships hidden in EVERY response and is unhidden only here.
  function showSuccess() {
    if (!successPanel) return;
    successPanel.hidden = false;
    if (form) form.hidden = true;
    successPanel.setAttribute('tabindex', '-1');
    successPanel.focus();
    try {
      successPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (e) {
      successPanel.scrollIntoView();
    }
  }

  // Read AND clear: nothing can replay it.
  function readStash() {
    var stash = null;
    try {
      var raw = sessionStorage.getItem(STASH_KEY);
      if (raw) stash = JSON.parse(raw);
      sessionStorage.removeItem(STASH_KEY);
    } catch (e) {}
    return stash;
  }

  // Thank-you, then (only for a live stash) the conversion + Klaviyo.
  function completeLead(stash, extra) {
    extra = extra || {};
    showSuccess();

    // No stash means this URL was typed, shared, bookmarked, or crawled.
    // Show the thank-you, fire nothing.
    if (!stash || !stash.t || (Date.now() - stash.t) > STASH_TTL_MS) return;

    track('generate_lead', {
      currency: 'USD',
      value: 0,
      lead_type: 'wholesale_matcha_application',
      business_type: stash.business_type || '(not set)',
      volume_band: stash.volume_band || '(not set)',
      ein_provided: !!stash.ein_provided,
      click_id_type: stash.click_id_type || '(none)',
      lead_confidence: 'stash_verified',
      submit_path: extra.submit_path || 'native'
    });

    if (stash.email) {
      var props = {
        source: 'Wholesale matcha landing page',
        business_type: stash.business_type || '',
        volume_band: stash.volume_band || '',
        hs_ein_provided: !!stash.ein_provided
      };
      if (extra.application_id) props.hs_application_id = String(extra.application_id);
      klaviyoLead(stash.email, props);
    }
  }

  function handleReturn() {
    var qs;
    try { qs = new URLSearchParams(window.location.search); } catch (e) { return; }
    if (qs.get('contact_posted') !== 'true') return;
    completeLead(readStash(), { submit_path: 'native' });
  }

  /* ---------- sticky CTA ---------- */

  function initSticky() {
    var sticky = root.querySelector('[data-wm-sticky]');
    var hero = root.querySelector('[data-wm-hero]');
    var formSection = document.getElementById('wholesale-application');
    if (!sticky || !hero) return;

    if (!('IntersectionObserver' in window)) return;

    var heroOut = false;
    var formIn = false;

    function update() {
      // Show once the hero CTA has scrolled away, hide again over the form itself.
      if (heroOut && !formIn) sticky.classList.add('is-visible');
      else sticky.classList.remove('is-visible');
    }

    new IntersectionObserver(function (entries) {
      heroOut = !entries[0].isIntersecting;
      update();
    }, { rootMargin: '0px' }).observe(hero);

    if (formSection) {
      new IntersectionObserver(function (entries) {
        formIn = entries[0].isIntersecting;
        update();
      }, { rootMargin: '0px' }).observe(formSection);
    }
  }

  /* ---------- CTA instrumentation ---------- */

  function initCtas() {
    var ctas = root.querySelectorAll('[data-wm-cta]');
    for (var i = 0; i < ctas.length; i++) {
      (function (el) {
        el.addEventListener('click', function () {
          track('wholesale_cta_click', {
            cta_location: el.getAttribute('data-wm-cta') || 'unknown',
            cta_text: (el.textContent || '').trim().slice(0, 60)
          });
        });
      })(ctas[i]);
    }

    var contacts = root.querySelectorAll('[data-wm-contact]');
    for (var j = 0; j < contacts.length; j++) {
      (function (el) {
        el.addEventListener('click', function () {
          track('wholesale_contact_click', {
            contact_method: el.getAttribute('data-wm-contact') || 'unknown'
          });
        });
      })(contacts[j]);
    }
  }

  /* ---------- anchor scroll: focus the section, not the first input ---------- */

  function initAnchors() {
    var links = root.querySelectorAll('a[href="#wholesale-application"]');
    var target = document.getElementById('wholesale-application');
    if (!target) return;
    for (var i = 0; i < links.length; i++) {
      links[i].addEventListener('click', function (ev) {
        ev.preventDefault();
        target.setAttribute('tabindex', '-1');
        try {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (e) {
          target.scrollIntoView();
        }
        // Focus the section so the mobile keyboard does not cover what was asked for.
        target.focus({ preventScroll: true });
      });
    }
  }

  /* ---------- boot ---------- */

  captureClickIds();
  initCtas();
  initAnchors();
  initSticky();
  handleReturn();

  track('wholesale_lp_view', { page_type: 'wholesale_landing_page' });
})();
